import * as yup from 'yup';
import { ChevronRight, Mail, Smartphone, User } from 'react-feather';
import { FormManager, useForm } from 'react-form-state-manager';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  OnCompletedLoginCallback, useLogin, useRequestLogin, useValidateUserEmail,
} from '../../common/Auth/useAuth';
import { emailRegex, getServerErrors } from '../../common/helpers';
import CompleteAuthenticatorLoginSessionMutation from './CompleteAuthenticatorLoginSessionMutation';
import UI from '../../common/UI';
import config from '../../config';
import useMutation from '../../api/useMutation';
import useValidation from '../../common/useValidation';

interface LoginFormData {
  email: string | null;
  participant_id: string | null;
  login_requested_for_email?: string;
}

const getRequestLoginSchema = (requireEmail: boolean) => yup.object().shape({
  email: (() => {
    let type = yup.string().ensure().matches(emailRegex, { excludeEmptyString: true, name: 'email' });

    if (requireEmail) {
      type = type.required();
    }

    return type;
  })(),
});

const loginSchema = yup.object().shape({
  pin: yup.string().required(),
});

const authenticatorSchema = yup.object().shape({
  code: yup.string().required(),
});

export interface LoginFormProps {
  onCompleted?: OnCompletedLoginCallback,
  title?: string,
  description?: string,
  scopedEmail?: string,
  scopedParticipantId?: string,
  loginRequestedForEmail?: string,
}

const LoginForm = ({
  onCompleted, title, description, scopedEmail, scopedParticipantId = null, loginRequestedForEmail,
}: LoginFormProps) => {
  const form = useForm<LoginFormData>({
    values: {
      email: scopedEmail || '',
      participant_id: scopedParticipantId,
      login_requested_for_email: loginRequestedForEmail,
    },
  });

  if (form.values.login_requested_for_email) {
    return (
      <ValidateLoginForm
        email={form.values.login_requested_for_email}
        participantId={form.values.participant_id}
        onCompleted={onCompleted}
        onCancel={() => form.delete('login_requested_for_email')}
      />
    );
  }

  return (
    <RequestLoginForm
      form={form}
      title={title}
      description={description}
    />
  );
};

interface RequestLoginFormProps {
  form: FormManager<LoginFormData>;
  title?: string,
  description?: string,
}

const RequestLoginForm = ({ form, title, description }: RequestLoginFormProps) => {
  const { t } = useTranslation();

  const [requestLogin, { error, loading }] = useRequestLogin({
    email: !form.values.participant_id ? form.values.email : null,
    participant_id: form.values.participant_id,
  }, {
    onCompleted: (email: string) => {
      form.set('login_requested_for_email', email);
    },
  });

  const requireEmail = !form.values.participant_id;

  const { valid, errors } = useValidation({
    schema: getRequestLoginSchema(requireEmail),
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  const { userExists, loading: validationLoading } = useValidateUserEmail({
    email: !form.values.participant_id ? form.values.email : null,
    participant_id: form.values.participant_id,
  }, {
    skip: !valid,
  });

  useEffect(() => {
    if (!requireEmail && !userExists && !validationLoading) {
      form.set('participant_id', null);
    }
  }, [requireEmail, userExists, validationLoading, form]);

  return (
    <UI.Form onSubmit={() => requestLogin()}>
      <UI.FormGrid>
        <UI.FormGrid sc={{ columns: '30px 1fr', gutter: 0.5, alignVertical: 'center' }}>
          <UI.Badge sc={{ size: 30, brand: 'primary.50', color: 'primary' }}>
            <UI.Icon sc={{ size: 1.5 }}>
              <User />
            </UI.Icon>
          </UI.Badge>
          <UI.H3>
            {title || t('log_in')}
          </UI.H3>
        </UI.FormGrid>

        <UI.Div>
          {description || t('enter_email_to_continue')}
        </UI.Div>

        {requireEmail && (
          <UI.InputGroup sc={{ valid: !errors.email, touched: form.touched.email }}>
            <UI.InputLabel htmlFor="Email">
              {t('email_address')}
            </UI.InputLabel>
            <UI.InputWrapper>
              <UI.DebouncedInput
                {...form.text('email')}
                inputMode="email"
                id="Email"
                autoFocus
                placeholder={t('common:email_placeholder')}
                sc={{ size: 'lg' }}
              />
            </UI.InputWrapper>
            <UI.ErrorMessages attribute={t('email_address')} errors={errors.email} />
          </UI.InputGroup>
        )}

        {valid && requireEmail && !userExists && !validationLoading && (
          <UI.Warning>
            {t('user_not_found')}
          </UI.Warning>
        )}

        <UI.Button
          type="submit"
          sc={{
            brand: 'primary', block: true, size: 'lg', loading, strong: true,
          }}
          disabled={!valid || !userExists || loading}
        >
          {requireEmail ? (
            <>
              {t('continue')}
              {' '}
              <UI.Icon>
                <ChevronRight />
              </UI.Icon>
            </>
          ) : (
            <>
              <UI.Icon>
                <Mail />
              </UI.Icon>
              {' '}
              {t('send_login_code')}
            </>
          )}
        </UI.Button>
      </UI.FormGrid>
    </UI.Form>
  );
};

interface ValidateLoginFormProps {
  email: string;
  participantId: string | null;
  onCompleted?: OnCompletedLoginCallback,
  onCancel: () => void;
}

const ValidateLoginForm = ({ email, participantId, onCompleted, onCancel }: ValidateLoginFormProps) => {
  const { t } = useTranslation();

  const form = useForm({
    values: {
      pin: '',
    },
  });

  const [showAuthenticatorLoginForm, setShowAuthenticatorLoginForm] = useState(false);

  const [login, { data, error, loading }] = useLogin(
    {
      email: !participantId ? email : null,
      participant_id: participantId,
    },
    form.values.pin,
    {
      onCompleted: (user, requireAuthenticatorCode) => {
        if (requireAuthenticatorCode) {
          setShowAuthenticatorLoginForm(true);
        } else {
          onCompleted?.(user, requireAuthenticatorCode);
        }
      },
    });

  const { errors } = useValidation({
    schema: loginSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  useEffect(() => {
    if (errors.login) {
      // There is something wrong with the login request, make the user start over.
      onCancel();
    }
  }, [errors.login, onCancel]);

  const [showingLoginCodeHelp, setShowingLoginHelp] = useState(false);

  useEffect(() => {
    if (form.values.pin.trim().length === config.loginEmailCodeLength) {
      login();
    }
  }, [form.values.pin, login]);

  if (showAuthenticatorLoginForm) {
    return <AuthenticatorLoginForm onCompleted={onCompleted} onCancel={onCancel} />;
  }

  return (
    <UI.Form onSubmit={() => login()}>
      <UI.FormGrid>
        <UI.FormGrid sc={{ columns: '30px 1fr', gutter: 0.5, alignVertical: 'center' }}>
          <UI.Badge sc={{ size: 30, brand: 'primary.50', color: 'primary' }}>
            <UI.Icon sc={{ size: 1.5 }}>
              <Mail />
            </UI.Icon>
          </UI.Badge>
          <UI.H3>
            {t('enter_your_login_code')}
          </UI.H3>
        </UI.FormGrid>

        <UI.Div>
          {t('login_requested', { email })}
          {' '}
          <UI.A onClick={() => setShowingLoginHelp((show) => !show)}>
            {t('did_not_receive_code')}
          </UI.A>
        </UI.Div>

        {showingLoginCodeHelp && (
          <UI.Info>
            {t('login_code_help')}
          </UI.Info>
        )}

        <UI.InputGroup sc={{ invalid: error && !!errors.pin }}>
          <UI.InputLabel htmlFor="Pin_0">
            {t('login_code')}
          </UI.InputLabel>
          <UI.PinCodeInput
            value={form.values.pin}
            onChange={(value) => form.set('pin', value)}
            length={config.loginEmailCodeLength}
            match={/[A-Z]/ig}
            name="pin"
            id="Pin"
            autoFocus
            placeholder="ABCDE"
            sc={{ size: 'lg' }}
          />
          <UI.ErrorMessages attribute={t('login_code')} errors={errors.pin} />
        </UI.InputGroup>

        <UI.Div>
          <UI.Button onClick={onCancel} sc={{ block: true, disabled: !!data || loading }}>
            {t('cancel')}
          </UI.Button>
        </UI.Div>
      </UI.FormGrid>
    </UI.Form>
  );
};

interface AuthenticatorLoginFormProps {
  onCompleted?: OnCompletedLoginCallback,
  onCancel: () => void;
}

const AuthenticatorLoginForm = ({ onCompleted, onCancel }: AuthenticatorLoginFormProps) => {
  const { t } = useTranslation();

  const form = useForm({
    values: {
      code: '',
    },
  });

  const [login, { data, error, loading }] = useMutation(CompleteAuthenticatorLoginSessionMutation, {
    variables: {
      input: {
        code: form.values.code,
      },
    },
    onCompleted: (data) => onCompleted?.(data.completeAuthenticatorLoginSession.user, false),
    onError: () => {},
  });

  const { errors } = useValidation({
    schema: authenticatorSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  useEffect(() => {
    if (errors.completeAuthenticatorLoginSession) {
      // There is something wrong with the login request, make the user start over.
      onCancel();
    }
  }, [errors.completeAuthenticatorLoginSession, onCancel]);

  useEffect(() => {
    if (form.values.code.trim().length === config.authenticatorCodeLength) {
      login();
    }
  }, [form.values.code, login]);

  return (
    <UI.Form onSubmit={() => login()}>
      <UI.FormGrid>
        <UI.FormGrid sc={{ columns: '30px 1fr', gutter: 0.5, alignVertical: 'center' }}>
          <UI.Badge sc={{ size: 30, brand: 'primary.50', color: 'primary' }}>
            <UI.Icon sc={{ size: 1.5 }}>
              <Smartphone />
            </UI.Icon>
          </UI.Badge>
          <UI.H3>
            {t('open_your_authenticator_app')}
          </UI.H3>
        </UI.FormGrid>

        <UI.Div>
          {t('enter_authenticator_code_description')}
        </UI.Div>

        <UI.InputGroup sc={{ invalid: error && !!errors.code }}>
          <UI.InputLabel htmlFor="Pin_0">
            {t('authenticator_code')}
          </UI.InputLabel>
          <UI.PinCodeInput
            value={form.values.code}
            onChange={(value) => form.set('code', value)}
            spacingAfter={[2]}
            length={config.authenticatorCodeLength}
            name="code"
            id="Pin"
            autoFocus
            placeholder="123456"
            inputMode="numeric"
            sc={{ size: 'lg' }}
          />
          <UI.ErrorMessages attribute={t('common:authenticator.authenticator_code')} errors={errors.code} />
        </UI.InputGroup>

        <UI.Div>
          <UI.Button onClick={onCancel} sc={{ block: true, disabled: !!data || loading }}>
            {t('cancel')}
          </UI.Button>
        </UI.Div>
      </UI.FormGrid>
    </UI.Form>
  );
};

export default LoginForm;
